import React, { useEffect } from 'react';
import { useStore } from '@xyflow/react';
import { X } from 'lucide-react';
import { cn } from '@cotera/client/v0/lib/utils';
import {
  WORKFLOW_INSPECTOR_HEIGHT,
  WORKFLOW_INSPECTOR_WIDTH,
  workflowInspectorPlacement,
} from './components/workflow-edit-layout';

/**
 * A panel floating over a workflow canvas, for inspecting whatever is selected.
 *
 * Overlaid rather than laid out beside the canvas, which is what keeps the graph
 * visible. Three reasons it beats splitting the panel into columns:
 *
 * - The runs panel already claims the bottom of the page. An overlay doesn't
 *   compete with it.
 * - It never has to fight the page's layout branches; it re-docks instead.
 * - The canvas never changes size, so React Flow's viewport doesn't shift. A
 *   column split resizes the canvas on every open and close, and React Flow won't
 *   re-fit on its own — the graph would appear to jump.
 *
 * It docks to the side when the canvas is wide enough to still show a node card
 * beside it, and to the bottom when it isn't — the default chat-first split leaves
 * the canvas around 600px, where a side panel would leave less room than one card
 * is wide. Read off the canvas, so every way of giving it more room — expanding
 * the panel, opening the workflow as its own page — earns the side dock alike.
 * See `workflowInspectorPlacement`.
 *
 * Occlusion is the trade, and the canvas pays it down by panning the target node
 * clear of this panel before it opens (see `useRevealNodeBesideInspector`).
 *
 * Renders as a child of `<Canvas>`, alongside `WorkflowEditorFloatingChrome`,
 * which established this floating-over-the-canvas pattern.
 *
 * Content-agnostic on purpose: the editor puts a node's config form in here, the
 * run graph puts a step's result. Everything this owns — placement, the wheel and
 * drag guards, Escape, the close button — is the same either way, and a second
 * copy of it would have drifted from the first.
 */
export const WorkflowCanvasInspector: React.FC<{
  open: boolean;
  onClose: () => void;
  /**
   * Slim header drawn by the shell. Omit it when the content brings its own
   * chrome — the editor's `WorkflowEditPanel` already has a header and a close
   * button, and a second set would just be duplicated.
   */
  header?: { title: string; actions?: React.ReactNode };
  children: React.ReactNode;
}> = ({ open, onClose, header, children }) => {
  const canvasWidth = useStore((state) => state.width);
  const placement = workflowInspectorPlacement(canvasWidth);

  // Escape closes. React Flow ignores key events from form fields, but this
  // listener would not, so bail when focus is inside an input.
  useEffect(() => {
    if (!open) {
      return;
    }
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key !== 'Escape') {
        return;
      }
      const active = document.activeElement;
      const isEditing =
        active instanceof HTMLInputElement ||
        active instanceof HTMLTextAreaElement ||
        (active instanceof HTMLElement && active.isContentEditable);
      if (isEditing) {
        return;
      }
      onClose();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [open, onClose]);

  if (!open) {
    return null;
  }

  return (
    <div
      data-slot="workflow-canvas-inspector"
      data-placement={placement}
      // `nowheel` is load-bearing: without it, scrolling long content zooms the
      // canvas instead. `nopan`/`nodrag` stop a drag inside the panel panning the
      // graph or dragging a node.
      className={cn(
        'nowheel nopan nodrag absolute z-20 flex flex-col overflow-hidden rounded-lg border border-border/80 bg-card shadow-lg',
        placement === 'side'
          ? 'top-3 right-3 bottom-3'
          : 'inset-x-3 bottom-3 max-h-[60%]'
      )}
      style={
        placement === 'side'
          ? { width: WORKFLOW_INSPECTOR_WIDTH }
          : { height: WORKFLOW_INSPECTOR_HEIGHT }
      }
    >
      {header !== undefined ? (
        <div className="flex shrink-0 items-center gap-1 border-b px-3 py-2">
          <span className="min-w-0 flex-1 truncate text-sm font-medium">
            {header.title}
          </span>
          {header.actions}
          <button
            type="button"
            title="Close"
            aria-label="Close"
            onClick={onClose}
            className="text-muted-foreground hover:bg-muted hover:text-foreground flex size-7 shrink-0 items-center justify-center rounded-md transition-colors"
          >
            <X className="size-4" />
          </button>
        </div>
      ) : null}
      {/* Positioned, because content may fill it via `absolute inset-0` — the
          editor's panel does exactly that. */}
      <div className="relative min-h-0 flex-1">{children}</div>
    </div>
  );
};
